// client/src/components/map/geo.js
// Shared geo helpers for the LexiStar MapLibre layer. Everything here works in
// GeoJSON [lng, lat] order EXCEPT isInsideGeofence / isPointInPolygon, which
// keep the [lat, lng] convention the geofence `Coordinates` column is stored in
// (matching the backend point-in-polygon check). Reused by Phase 1 (live map)
// and available to the later Trips / zone-editor phases.

const EARTH_RADIUS_M = 6378137; // WGS-84 equatorial radius

/**
 * Approximate a geodesic circle as a closed polygon ring in [lng, lat] order.
 * Uses a local equirectangular offset — accurate to well within a metre for the
 * tens-to-hundreds-of-metre radii geofences use, and needs no extra deps.
 * @returns {Array<[number,number]>} closed ring (first point repeated at end)
 */
export function circleToPolygonRing(lng, lat, radiusMeters, steps = 64) {
  if (lng == null || lat == null || !radiusMeters) return null;
  const latRad = (lat * Math.PI) / 180;
  const dLat = (radiusMeters / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLng = (radiusMeters / (EARTH_RADIUS_M * Math.cos(latRad))) * (180 / Math.PI);
  const ring = [];
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(theta), lat + dLat * Math.sin(theta)]);
  }
  return ring;
}

/**
 * Convert stored [lat, lng] polygon coordinates into a closed [lng, lat] ring.
 * @param {Array<[number,number]>} coords array of [lat, lng] pairs
 * @returns {Array<[number,number]>|null}
 */
export function polygonRingFromLatLngs(coords) {
  if (!Array.isArray(coords) || coords.length < 3) return null;
  const ring = coords.map(([la, ln]) => [ln, la]);
  const [f0, f1] = ring[0];
  const [l0, l1] = ring[ring.length - 1];
  if (f0 !== l0 || f1 !== l1) ring.push([f0, f1]);
  return ring;
}

/** Centroid of a geofence in [lng, lat] order (works for both types). */
export function geofenceCentroid(geofence) {
  if (geofence.GeofenceType === 'POLYGON') {
    const coords = geofence.Coordinates ? JSON.parse(geofence.Coordinates) : null;
    if (coords && coords.length >= 3) {
      const la = coords.reduce((s, c) => s + c[0], 0) / coords.length;
      const ln = coords.reduce((s, c) => s + c[1], 0) / coords.length;
      return [ln, la];
    }
  }
  return [geofence.Longitude, geofence.Latitude];
}

/**
 * A closed [lng, lat] ring for a geofence of either type, or null if it can't
 * be built. CIRCULAR zones are converted to a geodesic polygon.
 */
export function geofenceToRing(geofence) {
  if (geofence.GeofenceType === 'POLYGON') {
    const coords = geofence.Coordinates ? JSON.parse(geofence.Coordinates) : null;
    return polygonRingFromLatLngs(coords);
  }
  return circleToPolygonRing(geofence.Longitude, geofence.Latitude, geofence.RadiusMeters || 0);
}

/**
 * Ray-casting point-in-polygon. `polygon` is [lat, lng] pairs (stored order).
 */
export function isPointInPolygon(lat, lng, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0],
      yi = polygon[i][1];
    const xj = polygon[j][0],
      yj = polygon[j][1];
    const intersect = yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Whether a point is inside a geofence (POLYGON or CIRCULAR). */
export function isInsideGeofence(lat, lng, geofence) {
  if (geofence.GeofenceType === 'POLYGON') {
    const coords = geofence.Coordinates ? JSON.parse(geofence.Coordinates) : null;
    if (!coords || coords.length < 3) return false;
    return isPointInPolygon(lat, lng, coords);
  }
  const R = 6371000; // metres
  const dLat = ((lat - geofence.Latitude) * Math.PI) / 180;
  const dLng = ((lng - geofence.Longitude) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((geofence.Latitude * Math.PI) / 180) *
      Math.cos((lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c <= (geofence.RadiusMeters || 0);
}

/**
 * Bounding box for a set of [lng, lat] points, as a MapLibre LngLatBoundsLike
 * ([[west, south], [east, north]]). Returns null for an empty set.
 */
export function boundsFromLngLats(points) {
  if (!points || points.length === 0) return null;
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  for (const [lng, lat] of points) {
    if (lng == null || lat == null || Number.isNaN(lng) || Number.isNaN(lat)) continue;
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  if (minLng === Infinity) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/** Shortest-path angular interpolation (degrees). */
export function lerpAngle(a, b, t) {
  const d = ((b - a + 540) % 360) - 180;
  return a + d * t;
}
